import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Footer() {
  const { t } = useTranslation();

  const quickLinks = [
    { labelKey: 'nav.home', path: '/' },
    { labelKey: 'nav.about', path: '/about' },
    { labelKey: 'nav.tours', path: '/tours' },
    { labelKey: 'nav.gallery', path: '/gallery' },
    { labelKey: 'nav.testimonials', path: '/testimonials' },
    { labelKey: 'nav.certifications', path: '/certifications' },
    { labelKey: 'nav.contact', path: '/contact' },
  ];

  const destinationKeys = ['footer.d1','footer.d2','footer.d3','footer.d4','footer.d5','footer.d6'];

  return (
    <footer className="bg-emerald-900 text-white">
      <div className="max-w-7xl mx-auto px-6 pt-16 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 flex items-center justify-center bg-white rounded-lg">
                <img
                  src="https://static.readdy.ai/image/3294a4af19582454411e375cbd1c78be/9871cfdc49cd769f3d8dc69b76a1630b.png"
                  alt="Arus Travel & Tours"
                  className="w-10 h-10 object-contain"
                />
              </div>
              <div>
                <p className="font-bold text-white font-playfair leading-tight">Arus Travel & Tours</p>
                <p className="text-emerald-300 text-xs">Sdn Bhd</p>
              </div>
            </div>
            <p className="text-emerald-100/80 text-sm leading-relaxed mb-5">
              {t('footer.tagline')}
            </p>
            <div className="flex gap-3">
              {[
                { icon: 'ri-facebook-fill', href: 'https://www.facebook.com/arustravel', label: 'Facebook' },
                { icon: 'ri-instagram-line', href: 'https://www.instagram.com/arustravel', label: 'Instagram' },
                { icon: 'ri-whatsapp-line', href: 'https://wa.me/60176459709', label: 'WhatsApp' },
                { icon: 'ri-tiktok-fill', href: 'https://www.tiktok.com/@arustravel', label: 'TikTok' },
              ].map((social) => (
                <a
                  key={social.icon}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  aria-label={social.label}
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-emerald-800 hover:bg-amber-500 transition-colors cursor-pointer"
                >
                  <i className={`${social.icon} text-sm`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-white mb-5 text-sm uppercase tracking-wider">
              <a href="#quick-links" className="hover:text-amber-400 transition-colors">{t('footer.quickLinks')}</a>
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-emerald-100/70 hover:text-amber-400 text-sm transition-colors cursor-pointer flex items-center gap-2"
                  >
                    <i className="ri-arrow-right-s-line text-amber-500"></i>
                    {t(link.labelKey)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Destinations */}
          <div>
            <h4 className="font-semibold text-white mb-5 text-sm uppercase tracking-wider">
              <a href="#destinations" className="hover:text-amber-400 transition-colors">{t('footer.destinations')}</a>
            </h4>
            <ul className="space-y-3">
              {destinationKeys.map((key) => (
                <li key={key}>
                  <Link
                    to="/tours"
                    className="text-emerald-100/70 hover:text-amber-400 text-sm transition-colors cursor-pointer flex items-center gap-2"
                  >
                    <i className="ri-arrow-right-s-line text-amber-500"></i>
                    {t(key)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-white mb-5 text-sm uppercase tracking-wider">
              <a href="#contact-info" className="hover:text-amber-400 transition-colors">{t('footer.contactUs')}</a>
            </h4>
            <ul className="space-y-4">
              <li className="flex gap-3 items-start">
                <div className="w-8 h-8 flex items-center justify-center text-amber-400 flex-shrink-0 mt-0.5">
                  <i className="ri-map-pin-line text-base"></i>
                </div>
                <p className="text-emerald-100/70 text-sm leading-relaxed">
                  Level 7, Menara Safuan<br />No. 80, Jalan Ampang,<br />50450 Kuala Lumpur, Malaysia
                </p>
              </li>
              <li className="flex gap-3 items-center">
                <div className="w-8 h-8 flex items-center justify-center text-amber-400 flex-shrink-0">
                  <i className="ri-phone-line text-base"></i>
                </div>
                <a href="tel:+60176459709" className="text-emerald-100/70 hover:text-amber-400 text-sm transition-colors">
                  017 645 9709
                </a>
              </li>
              <li className="flex gap-3 items-center">
                <div className="w-8 h-8 flex items-center justify-center text-amber-400 flex-shrink-0">
                  <i className="ri-mail-line text-base"></i>
                </div>
                <a href="mailto:info@arustravel.com.my" className="text-emerald-100/70 hover:text-amber-400 text-sm transition-colors">
                  info@arustravel.com.my
                </a>
              </li>
              <li className="flex gap-3 items-center">
                <div className="w-8 h-8 flex items-center justify-center text-amber-400 flex-shrink-0">
                  <i className="ri-time-line text-base"></i>
                </div>
                <p className="text-emerald-100/70 text-sm">{t('footer.hours')}</p>
              </li>
            </ul>
          </div>
        </div>

        {/* Certifications */}
        <div className="border-t border-emerald-800 pt-8 mb-8">
          <div className="flex flex-wrap items-center gap-6 justify-center">
            {['MATTA Member', 'IATA Accredited', 'Tourism Malaysia', 'MOT Licensed'].map((cert) => (
              <div
                key={cert}
                className="flex items-center gap-2 bg-emerald-800 px-4 py-2 rounded-full"
              >
                <i className="ri-verified-badge-fill text-amber-400 text-sm"></i>
                <span className="text-emerald-100/80 text-xs font-medium">{cert}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-emerald-800 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-emerald-100/50 text-xs text-center sm:text-left">
            &copy; {new Date().getFullYear()} Arus Travel & Tours Sdn Bhd (803090-A). All rights reserved.
          </p>
          <div className="flex gap-4">
            <a href="#" className="text-emerald-100/50 hover:text-emerald-100 text-xs transition-colors" rel="nofollow">{t('footer.privacy')}</a>
            <a href="#" className="text-emerald-100/50 hover:text-emerald-100 text-xs transition-colors" rel="nofollow">{t('footer.terms')}</a>
          </div>
        </div>

        {/* Powered by */}
        <div className="mt-4 text-center">
          <p className="text-emerald-100/30 text-xs">
            Powered by <span className="text-emerald-100/50 font-medium">Kitacomm Technology Sdn Bhd</span>
          </p>
        </div>
      </div>
    </footer>
  );
}
